import React from 'react';
import styled from 'styled-components';
import { Body } from '@grnhse/seedling/lib/birch/typography';
import Checkbox from '@grnhse/seedling/lib/birch/components/checkbox';
import ArrowUpIcon from '@grnhse/seedling/lib/birch/icons/increase_arrow';
import ArrowDownIcon from '@grnhse/seedling/lib/birch/icons/decrease_arrow';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import { withLocalePrefix } from 'shared/utils/translation';
import { CalibrationSuggestion } from 'talent_matching/types';

const t = withLocalePrefix('talent_matching.modals.calibration_nudge');
const tImportance = withLocalePrefix(
  'talent_matching.modals.calibration.importance'
);

const IMPORTANCE_KEY_MAP: Record<number, string> = {
  1: 'not_very_important',
  2: 'somewhat_important',
  3: 'important',
  4: 'very_important',
  5: 'crucial',
};

const importanceLabel = (value: number) =>
  tImportance(IMPORTANCE_KEY_MAP[value] ?? 'important');

const SuggestionsContainer = styled.div`
  background-color: ${FullPalette.GREY_E50};
  border: 1px solid ${FullPalette.GREY_700};
  border-radius: 4px;
  padding: ${Tokens.spacing[2]};
  display: flex;
  flex-direction: column;
  gap: ${Tokens.spacing[1]};
`;

const HorizontalDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${FullPalette.GREY_700};
`;

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Tokens.spacing[1]};
`;

const SkillCard = styled.div<{ $checked: boolean }>`
  background: white;
  border: ${(props) =>
    props.$checked
      ? `1px solid ${FullPalette.GREEN_700}`
      : `1px solid ${FullPalette.GREY_700}`};
  border-radius: 4px;
  padding: ${Tokens.spacing['1.5']};
  display: flex;
  flex-direction: column;
  gap: ${Tokens.spacing[1]};
`;

const SkillMeta = styled.div`
  padding-left: 34px;
  display: flex;
  flex-direction: column;
  gap: ${Tokens.spacing[1]};
`;

const SelectAllWrapper = styled.div`
  padding-left: ${Tokens.spacing['1.5']};
`;

const MetaLine = styled.div`
  display: flex;
  align-items: center;
  gap: ${Tokens.spacing['0.5']};
`;

type Props = {
  suggestions: CalibrationSuggestion[];
  checkedNames: Set<string>;
  allChecked: boolean;
  someChecked: boolean;
  toggleAll: () => void;
  toggleOne: (name: string) => void;
};

const NudgeSuggestionsNarrow = ({
  suggestions,
  checkedNames,
  allChecked,
  someChecked,
  toggleAll,
  toggleOne,
}: Props) => (
  <SuggestionsContainer>
    <SelectAllWrapper>
      <Checkbox
        label={t('actions.select_all')}
        value="select-all"
        checked={allChecked}
        indeterminate={someChecked}
        onChange={toggleAll}
      />
    </SelectAllWrapper>
    <HorizontalDivider />
    <CardList>
      {suggestions.map((suggestion) => {
        const isChecked = checkedNames.has(suggestion.name);
        const direction =
          suggestion.suggested_importance > suggestion.current_importance
            ? 'up'
            : 'down';
        const iconColor =
          direction === 'up' ? FullPalette.GREEN_700 : FullPalette.POPPY_600;
        const ArrowIcon = direction === 'up' ? ArrowUpIcon : ArrowDownIcon;

        return (
          <SkillCard key={suggestion.name} $checked={isChecked}>
            <Checkbox
              label={suggestion.name}
              value={suggestion.name}
              checked={isChecked}
              onChange={() => toggleOne(suggestion.name)}
            />
            <SkillMeta>
              <MetaLine>
                <Body as="span">
                  {t('columns.current')}
                  {':'}
                </Body>
                <Body as="span" metadata>
                  {importanceLabel(suggestion.current_importance)}
                </Body>
              </MetaLine>
              <MetaLine>
                <Body as="span">
                  {t('columns.suggested')}
                  {':'}
                </Body>
                <ArrowIcon size="sm" color={iconColor} />
                <Body as="span" medium>
                  {importanceLabel(suggestion.suggested_importance)}
                </Body>
              </MetaLine>
            </SkillMeta>
          </SkillCard>
        );
      })}
    </CardList>
  </SuggestionsContainer>
);

export default NudgeSuggestionsNarrow;
